// ============================================================================
// NotificationService
// ============================================================================
// Centralized wrapper around react-toastify. Viewmodels and other application
// code should import from here rather than from "react-toastify" directly, so
// the underlying toast implementation can be swapped or customized in one
// place.
//
// Safe by default: `update` and `dismiss` check `isActive` before operating,
// avoiding race-condition errors when a toast was already closed (formerly
// handled by SafeToast).

import type { ReactNode } from "react";
import { toast, Id, ToastOptions, UpdateOptions } from "react-toastify";

export type NotificationId = Id;
export type { ToastOptions as NotificationOptions, UpdateOptions as NotificationUpdateOptions };

export const Notifications = {
	success(message: ReactNode, options?: ToastOptions): NotificationId {
		return options ? toast.success(message, options) : toast.success(message);
	},

	error(message: ReactNode, options?: ToastOptions): NotificationId {
		return options ? toast.error(message, options) : toast.error(message);
	},

	warning(message: ReactNode, options?: ToastOptions): NotificationId {
		return options ? toast.warning(message, options) : toast.warning(message);
	},

	info(message: ReactNode, options?: ToastOptions): NotificationId {
		return options ? toast.info(message, options) : toast.info(message);
	},

	/**
	 * Show a loading-state notification. Returns an id the caller can update
	 * (for progress) or dismiss once the operation completes.
	 */
	loading(message: ReactNode, options?: ToastOptions): NotificationId {
		return options ? toast.loading(message, options) : toast.loading(message);
	},

	/**
	 * Update an active notification. No-op if it has already been dismissed.
	 */
	update(id: NotificationId, options: UpdateOptions): void {
		if (toast.isActive(id)) {
			toast.update(id, options);
		}
	},

	/**
	 * Dismiss an active notification. No-op if it has already been dismissed.
	 * Call without an id to dismiss all notifications.
	 */
	dismiss(id?: NotificationId): void {
		if (id == null) {
			toast.dismiss();
			return;
		}
		if (toast.isActive(id)) {
			toast.dismiss(id);
		}
	},

	isActive(id: NotificationId): boolean {
		return toast.isActive(id);
	},
};
